import { FormEvent, useEffect, useState } from 'react';
import useForm, {
  FormValues,
  UseFormConfiguration,
  UseFormResult,
} from './useForm';
import getMaterialInputsFormHelper, {
  GetMaterialInputsFormHelperOptions,
  MaterialInputsFormHelper,
} from '../utils/getMaterialInputsFormHelper';
import { useNavigationGuardBlocker } from './useNavigationGuard';
import { useCancelChangesRequester } from '../contexts/cancelChangesRequester';

export interface UseIotsAdminFormConfiguration<T extends FormValues>
  extends Omit<UseFormConfiguration<T>, 'onSubmit'> {
  isEditingByDefault?: boolean;
  onSubmit: (values: T, finishEdition: () => void, event?: FormEvent) => void;
  onEditionCancel?: (finishEdition: () => void) => void;
}

export interface UseFormCustomResult<T extends FormValues>
  extends UseFormResult<T> {
  requestEdition: () => void;
  cancelEdition: () => void;
  isEditing: boolean;
}

export interface UseIotsAdminFormResult<T extends FormValues> {
  formState: UseFormCustomResult<T>;
  materialInputHelpers: MaterialInputsFormHelper<T>;
}

/** Complete behavior for an usual Admin form in the IOTS application:
 * - Complete form values management (validation, submission, resetting).
 * - Readonly/Edit mode state management.
 * - Navigation and cancelation confirmation when changes are going to be lost due to user interaction.
 * - Helper utilities to obtain appropriate props for Material UI input components.
 *
 * This hook is meant for form that use/need all the detailed features above. For simpler forms, consider using the separate
 * hooks and utilities:
 * - useForm
 * - getMaterialInputsHelper
 * - useNavigationGuard
 * - useCancelChangesRequester
 */
const useIotsAdminForm = <T extends FormValues>(
  formConfiguration: UseIotsAdminFormConfiguration<T>,
  editOptions = {} as Partial<GetMaterialInputsFormHelperOptions>,
): UseIotsAdminFormResult<T> => {
  const {
    initialValues,
    onSubmit,
    differenceChecker,
    isEditingByDefault = false,
    onEditionCancel: onCancel,
    resetOnInitialValuesChange,
    validate,
  } = formConfiguration;

  const [isEditing, setIsEditing] = useState(isEditingByDefault);

  const formState = useForm({
    initialValues,
    onSubmit: (values, event) => {
      if (!isEditing) {
        return;
      }
      onSubmit(values, () => setIsEditing(false), event);
    },
    validate,
    resetOnInitialValuesChange,
    differenceChecker,
  });

  useNavigationGuardBlocker(() => isEditing && formState.haveValuesChanged);

  const requestCancelChanges = useCancelChangesRequester();

  const materialInputHelpers = getMaterialInputsFormHelper(formState, {
    ...editOptions,
    shouldInputsBeDisabled: Boolean(
      !isEditing || editOptions.shouldInputsBeDisabled,
    ),
  });

  useEffect(() => {
    if (!isEditing && formState.haveValuesChanged) {
      formState.reset();
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEditing, formState.haveValuesChanged]);

  return {
    formState: {
      ...formState,
      isEditing,
      requestEdition: () => setIsEditing(true),
      cancelEdition: () => {
        const confirmEditionCancellation = () => {
          if (formState.haveValuesChanged) {
            requestCancelChanges(() => setIsEditing(false));
          } else {
            setIsEditing(false);
          }
        };

        if (onCancel) {
          onCancel(confirmEditionCancellation);
        } else {
          confirmEditionCancellation();
        }
      },
    },
    materialInputHelpers,
  };
};

export default useIotsAdminForm;
